import React, { useState } from 'react';
import { Form, Row, Col } from 'antd';
import FormLabelWithField from '../../Core/FormLabelWithField';
import Button from 'components/Core/Button';
import Select from '../../Core/Select';
import TextField from '../../Core/TextField';
import GatedSubscribeForm from '../GatedSubscribeForm';
import { HvacFormContainer, FormContainer } from './styles';
import EmbedForm from '../EmbeddableForm';
import EmbedLink from '../EmbedLink';
import Text from '../../Core/Text';

import { calcEnergySavings } from './calcs';
import { AvgElectricityRate, ClimateRegion, CapacityRange, CurrentSeer, NewSeer } from './content';

const SeerEnergyForm = ({ gatedForm, ...otherProps }) => {
  const [savings, setSavings] = useState(0);
  const [roi5yr, setRoi5yr] = useState(0);
  const [roi10yr, setRoi10yr] = useState(0);
  const [roi15yr, setRoi15yr] = useState(0);
  const [newCost, setNewCost] = useState(0);
  const [currentCost, setCurrentCost] = useState(0);
  const [currentSeer, setCurrentSeer] = useState(0);
  const [newSeer, setNewSeer] = useState(0);
  const [btu, setBtu] = useState(0);

  const nameType = {
    mod_location: 'Location of Install',
    mod_capacity: 'AC Capacity',
    mod_current: 'Current SEER',
    mod_new: 'New SEER',
    mod_electRate: 0,
  };

  const [values, setValues] = useState({
    mod_location: '',
    mod_capacity: '',
    mod_current: '',
    mod_new: '',
    mod_savings: 0,
    mod_new_cost: 0,
    mod_current_cost: 0,
    mod_5yr: 0,
    mod_10yr: 0,
    mod_15yr: 0,
    mod_electRate: 0,
  });

  const [formValid, setFormValid] = useState({
    mod_location: 0,
    mod_electRate: 0,
    mod_capacity: 0,
    mod_current: 0,
    mod_new: 0,
  });

  const [gateFormCookieIncrease, setGateFormCookieIncrease] = useState(0);
  const [isRecalc, setRecalc] = useState(true);

  const checkFieldValidate = (id, value) => {
    const fieldValidation = formValid;
    fieldValidation[id] = value !== '' ? 1 : -1;

    setFormValid(fieldValidation);
    return fieldValidation[id];
  };

  const checkValidate = () => {
    const keys = Object.keys(values);
    const stepValid = keys.filter(element => checkFieldValidate(element, values[element]) === 1)
      .length;
    return stepValid === keys.length;
  };

  const onHandleChange = (id, newValue) => {
    const tmpValues = Object.assign({}, values);
    tmpValues[id] = newValue;

    setValues(tmpValues);

    checkFieldValidate(id, newValue);
  };

  const onLocationChange = (id, newValue) => {
    const tmpValues = Object.assign({}, values);
    tmpValues[id] = newValue;
    tmpValues['mod_electRate'] =
      AvgElectricityRate[ClimateRegion.find(f => f.value === newValue).label];

    setValues(tmpValues);

    checkFieldValidate(id, newValue);
  };

  const calcAll = () => {
    if (!checkValidate() || !isRecalc) {
      return;
    }

    const { mod_location, mod_electRate, mod_capacity, mod_current, mod_new } = values;

    dataLayer.push({
      event: 'calculator',
      calculator_type: 'HVAC SEER',
      climate_region: mod_location,
      avg_electricity_rate: mod_electRate,
      ac_capacity: mod_capacity,
      current_seer: mod_current,
      new_seer: mod_new,
    });

    setGateFormCookieIncrease(gateFormCookieIncrease + 1);
    const calculatedResults = calcEnergySavings(
      values['mod_location'],
      values['mod_electRate'],
      values['mod_capacity'],
      values['mod_current'],
      values['mod_new']
    );
    setSavings(calculatedResults['SavingsPctPerYr']);
    setRoi5yr(calculatedResults['5yr']);
    setRoi10yr(calculatedResults['10yr']);
    setRoi15yr(calculatedResults['15yr']);
    setNewCost(calculatedResults['NewAnnualCost']);
    setCurrentCost(calculatedResults['CurrentAnnualCost']);
    setCurrentSeer(values['mod_current']);
    setNewSeer(values['mod_new']);
    setBtu(values['mod_capacity']);
  };

  return (
    <FormContainer isEmbed={otherProps.isEmbed} {...otherProps}>
      <div className="section-tool-page-form--wrapper">
        <div className="section-tool-page-form--container">
          <HvacFormContainer>
            <Row>
              <Col lg={12} md={12} sm={24} xs={24}>
                <GatedSubscribeForm gatedForm={gatedForm} increaseCookie={gateFormCookieIncrease}>
                  <div className="hvac-form-right-gate-container">
                    <div className="hvac-form-total-calculation">
                      <Form className="hvac-form--form" data-position="hvac seer form">
                        <Form.Item className="hvac-form--form-item">
                          <FormLabelWithField label="Location of Install" isRequired={false}>
                            <Select
                              className="seers-calc-drop-down"
                              id="mod_location"
                              nameType={nameType['mod_location']}
                              formValid={formValid}
                              placeholder="Select climate region"
                              options={ClimateRegion}
                              handleChange={onLocationChange}
                            />
                          </FormLabelWithField>
                        </Form.Item>

                        {values['mod_location'] !== '' && (
                          <Form.Item className="hvac-form--form-item">
                            <FormLabelWithField
                              label="Average cost of electricity (KWH)"
                              isRequired={false}
                            >
                              <TextField
                                className="seers-calc-drop-down"
                                id="mod_electRate"
                                type="number"
                                nameType={nameType['mod_electRate']}
                                formValid={formValid}
                                values={values}
                                handleChange={onHandleChange}
                                showOnlyFormError
                              />
                            </FormLabelWithField>
                            <p className="hvac-form--electrate-description">
                              Based on rates listed May 2021
                            </p>
                          </Form.Item>
                        )}

                        <Form.Item className="hvac-form--form-item">
                          <FormLabelWithField label="AC Capacity" isRequired={false}>
                            <Select
                              className="seers-calc-drop-down"
                              id="mod_capacity"
                              nameType={nameType['mod_capacity']}
                              formValid={formValid}
                              placeholder="Select AC capacity"
                              options={CapacityRange}
                              handleChange={onHandleChange}
                            />
                          </FormLabelWithField>
                        </Form.Item>

                        <Form.Item className="hvac-form--form-item">
                          <FormLabelWithField
                            label="Current SEER"
                            isRequired={false}
                            helperText="The Seasonal Energy Efficiency Ratio (SEER) is an energy efficiency rating for air conditioners. 
                                        The higher the SEER, the better the energy performance, the more you save."
                          >
                            <Select
                              className="seers-calc-drop-down"
                              id="mod_current"
                              nameType={nameType['mod_current']}
                              formValid={formValid}
                              placeholder="Select Current SEER"
                              options={CurrentSeer}
                              handleChange={onHandleChange}
                            />
                          </FormLabelWithField>
                        </Form.Item>

                        <Form.Item className="hvac-form--form-item">
                          <FormLabelWithField
                            label="New SEER"
                            isRequired={false}
                            helperText="The Seasonal Energy Efficiency Ratio (SEER) is an energy efficiency rating for air conditioners. 
                                        The higher the SEER, the better the energy performance, the more you save."
                          >
                            <Select
                              className="seers-calc-drop-down"
                              id="mod_new"
                              nameType={nameType['mod_new']}
                              formValid={formValid}
                              placeholder="Select New SEER"
                              options={NewSeer}
                              handleChange={onHandleChange}
                            />
                          </FormLabelWithField>
                        </Form.Item>
                      </Form>
                      <Button
                        dataPosition="Form - Calculate CTA"
                        className="hvac-form-calc-button"
                        onClick={() => calcAll()}
                        disabled={!isRecalc}
                      >
                        Calculate
                      </Button>
                      <div className="form-embed-link">
                        <EmbedLink
                          show={!otherProps.isEmbed}
                          openCopyEmbedLinkDialog={otherProps?.openCopyEmbedLinkDialog}
                          className="form-embed-link"
                        />
                      </div>
                    </div>
                  </div>
                </GatedSubscribeForm>
              </Col>
              <Col lg={12} md={12} sm={24} xs={24} className="sticky-container">
                <div className="hvac-form-right-container">
                  <Text type={'h5'} color={'black'} className={'seers-results-heading'}>
                    Cooling Savings
                  </Text>
                  <Text type={'p'} color={'black'} className={'seers-results-sub-heading'}>
                    {currentSeer} SEER vs {newSeer} SEER on a {btu} BTU Air Conditioner
                  </Text>
                  <div className="savings-circle-graph-container">
                    <div className="savings-item-column">
                      <svg viewBox="0 0 36 36" className="circular-chart-bottom">
                        <path
                          className="circle-bottom"
                          strokeDasharray="100, 100"
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <svg viewBox="0 0 36 36" className="circular-chart">
                        <path
                          className="circle"
                          strokeDasharray={savings + ', 100'}
                          d="M18 2.0845
                            a 15.9155 15.9155 0 0 1 0 31.831
                            a 15.9155 15.9155 0 0 1 0 -31.831"
                        />
                      </svg>
                      <div className="savings-percent-text-container">
                        <Text type={'h3'}>{savings}%</Text>
                        <Text type={'p'}>Savings Per Year</Text>
                      </div>
                    </div>
                    <div className="savings-item-column">
                      <Text type={'h6'} color={'black'}>
                        Annual Energy Costs
                      </Text>
                      <Text type={'p'} color={'black'}>
                        New Costs:
                        <span className="savings-result-span savings-new-cost"> ${newCost}</span>
                      </Text>
                      <Text type={'p'} color={'black'}>
                        Current Costs:
                        <span className="savings-result-span"> ${currentCost}</span>
                      </Text>
                    </div>
                  </div>
                  <div className="savings-bar-graph-container">
                    <ul className="savings-bar-graph">
                      <li>
                        <span style={{ height: '20%' }} title="5-Yrs" value={'$' + roi5yr}></span>
                      </li>
                      <li>
                        <span style={{ height: '50%' }} title="10-Yrs" value={'$' + roi10yr}></span>
                      </li>
                      <li>
                        <span style={{ height: '75%' }} title="15-Yrs" value={'$' + roi15yr}></span>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          </HvacFormContainer>
        </div>
      </div>
    </FormContainer>
  );
};

export const SeerEnergyCalc = EmbedForm(SeerEnergyForm);
